import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User, onAuthStateChanged } from "firebase/auth";
import { auth, authService } from "@/services/firebase";
import api from "@/services/api";

// Utility function to determine tenant ID from email
function getTenantIdFromEmail(email: string): string {
  const domain = email.split("@")[1];

  const domainToTenant: Record<string, string> = {
    "asepha.ai": "asepha",
    "me.com": "asepha", // Adding your domain
    "example.com": "example",
  };

  return domainToTenant[domain] || "asepha";
}

interface UserData {
  uid: string;
  email: string;
  displayName?: string;
  tenantId: string;
  role?: string;
  permissions?: string[];
  photoURL?: string;
}

interface AuthContextType {
  user: User | null;
  userData: UserData | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<User>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setUser(firebaseUser);
        if (firebaseUser) {
          // Set the authorization header for API calls
          const token = await firebaseUser.getIdToken();
          if (api && api.defaults && api.defaults.headers) {
            api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          }

          // Fetch additional user data from backend
          try {
            const baseUrl = import.meta.env.VITE_API_URL 
            console.log("baseUrl", baseUrl);
            const response = await fetch(`${baseUrl}/api/auth/me`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const responseData = await response.json();
              if (responseData.success && responseData.user) {
                const data = responseData.user;
                setUserData({
                  uid: firebaseUser.uid,
                  email: firebaseUser.email!,
                  displayName: data.displayName || firebaseUser.displayName,
                  tenantId: data?.tenantId || "asepha",
                  role: data.role,
                  permissions: data.permissions || [],
                  photoURL: data.photoURL || firebaseUser.photoURL,
                });
              } else {
                console.warn("No user data returned from backend");
              }
            } else {
              throw new Error(
                `HTTP ${response.status}: ${response.statusText}`,
              );
            }
          } catch (error) {
            console.error("Error fetching user data from backend:", error);
            // Fallback: try to get basic data from Firebase user
            setUserData({
              uid: firebaseUser.uid,
              email: firebaseUser.email!,
              displayName: firebaseUser.displayName || "",
              tenantId: getTenantIdFromEmail(firebaseUser.email || ""),
              role: "user", 
              permissions: [],
              photoURL: firebaseUser.photoURL || "",
            });
          }
        } else {
          // Remove authorization header and clear user data
          if (api && api.defaults && api.defaults.headers) {
            delete api.defaults.headers.common["Authorization"];
          }
          setUserData(null);
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string): Promise<User> => {
    try {
      const user = await authService.signIn(email, password);
      return user;
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    displayName?: string,
    role?: string,
  ): Promise<User> => {
    try {
      const user = await authService.signUp(email, password, displayName, role);
      return user;
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut();
      setUser(null);
      setUserData(null);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await authService.resetPassword(email);
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!user) return null;

    try {
      return await authService.getIdToken(user);
    } catch (error) {
      console.error("Get ID token error:", error);
      return null;
    }
  };

  const refreshUserData = async (): Promise<void> => {
    if (!user) return;

    try {
      const response = await api.get("/api/auth/me");
      let data = response.data.user;

      // Check if user data is missing required fields
      const missingFields = [];
      if (!data.role) missingFields.push('role');
      if (!data.tenantId) missingFields.push('tenantId');
      if (!data.type) missingFields.push('type');
      if (!data.status) missingFields.push('status');

      // If any required fields are missing, fix the user data
      if (missingFields.length > 0) {

        try {
          const fixResponse = await api.post("/api/auth/fix-user-data");
          if (fixResponse.data.success) {
            console.log("✅ User data fixed successfully:", fixResponse.data.fixedFields);
            data = fixResponse.data.user; // Use the fixed user data
          }
        } catch (fixError) {
          console.error("❌ Failed to fix user data:", fixError);
          // Continue with original data if fix fails
        }
      }

      if (response.data.success && data) {
        setUserData({
          uid: user.uid,
          email: user.email!,
          displayName: data.displayName || user.displayName,
          tenantId: data.tenantId,
          role: data.role,
          permissions: data.permissions || [],
          photoURL: data.photoURL || user.photoURL,
        });
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  const value: AuthContextType = {
    user,
    userData,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    getIdToken,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
